/* eslint-disable react/prop-types */
import React, { useState } from "react";
import {
  Box,
  Typography,
  Popover,
  List,
  ListItem,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import { BiChevronDown } from "react-icons/bi";
import buildRequestUrl from "lib/requests/requestUtils";
import athenaClient from "lib/axios/clients";
import { groupByPriceAndReduceEstimations } from "lib/filters/estimations";
import _, { forEach } from "lodash";

const useStyles = makeStyles((theme) => ({
  padding: {
    padding: theme.spacing(1.5),
  },
  alignCenterFlex: {
    display: "flex",
    alignItems: "center",
  },
  popoverRoot: {
    marginTop: "-5px",
  },
  listRoot: {
    padding: "0px",
  },
  listItem: {
    fontSize: "16px",
    minWidth: "300px",
    padding: "20px",
    "&::before": {
      content: `""`,
      position: "absolute",
      width: "calc(100% - 30px)",
      height: "1px",
      backgroundColor: "#ECEFF1",
      top: "0",
      left: "15px",
    },
    "&:hover": {
      backgroundColor: "#ECEFF1",
      cursor: "pointer",
    },
    "& > :last-child": {
      marginLeft: "10px",
    },
  },
  filterTitleAndIcon: {
    display: "flex",
    alignItems: "center",
  },
  popoverTitle: {
    padding: "20px",
  },
  selectValueContainer: {
    padding: "1px 1px 1px 30px",
    border: "1px solid #e2e2e2",
    borderRadius: "5px",
    boxShadow: "0 1px 4px 0 rgba(37, 50, 56, 0.1)",
    display: "flex",
    flexWrap: "wrap",
    "&:hover": {
      cursor: "pointer",
    },
    "& > :last-child": {
      textTransform: "capitalize",
      marginLeft: "5px",
    },
  },
}));

const StyledBox = withStyles(() => ({
  root: {
    flex: 1,
    direction: "ltr",
    flexDirection: "row",
    marginRight: "20px",
  },
}))(Box);

const SelectOptionsComponent = ({
  selectValue,
  setSelectValue,
  selectOptions,
  selectTitle,
  setCurrentTupleData,
  currentTupleData,
  metadataSlice,
  row,
  cycleId,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSelectClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleListItemClick = async (value) => {
    const tupleRequestParams = {
      filters: {
        product: row.product,
        ...(row.retailer && { retailer: row.retailer }),
        ...(row.retailer_group && { retailer_group: row.retailer_group }),
        ...(row.area && { area: row.area }),
        data_source_id: 1,
        cycle: { id: cycleId },
      },
    };
    let estimationsUrl = {};
    estimationsUrl = buildRequestUrl(
      {
        ...tupleRequestParams,
        group_by: ["price"],
        sum: ["units", "margin", "units_in", "sales"],
        avg_weight_by_units_in: ["price_in"],
      },
      "estimations_by_cycle"
    );
    const estimationsConfig = {
      method: "GET",
      url: estimationsUrl,
    };
    const response = await athenaClient(estimationsConfig);

    if (value.key !== null) {
      const estimationsDeltasUrl = buildRequestUrl(
        {
          ...tupleRequestParams,
          group_by: ["price"],
          sum: ["delta_units", "delta_margin", "delta_units_in", "delta_sales"],
          avg_weight_by_delta_units: "event_price",
          avg_weight_by_units_in: ["price_in"],
          expand: "delta_events_by_cycle",
          data_source_id: 1,
          event_id: value.key,
        },
        "estimations_by_cycle"
      );

      const estimationsDeltasConfig = {
        method: "GET",
        url: estimationsDeltasUrl,
      };
      const responseDeltas = await athenaClient(estimationsDeltasConfig);
      const tupleDataWithDeltas = [];
      forEach(response.data, (estimation) => {
        const matchedDelta = _.find(responseDeltas.data, {
          price: estimation.price,
        });
        tupleDataWithDeltas.push({ ...estimation, ...matchedDelta });
      });

      setCurrentTupleData({
        estimations: _.sortBy(
          groupByPriceAndReduceEstimations(
            tupleDataWithDeltas.map((obj) => {
              // Destructure the object to extract the old key value
              // eslint-disable-next-line camelcase
              const { avg_weight_by_units_in_price_in, ...rest } = obj;
              // Create a new object with the renamed key and the rest of the properties
              return { price_in: avg_weight_by_units_in_price_in, ...rest };
            }),
            metadataSlice["custom-projection"],
            "sum_"
          ),
          "price"
        ),
        crossAdjustments: currentTupleData.crossAdjustments,
      });
    } else {
      setCurrentTupleData({
        estimations: _.sortBy(
          groupByPriceAndReduceEstimations(
            response.data.map((obj) => {
              // Destructure the object to extract the old key value
              // eslint-disable-next-line camelcase
              const { avg_weight_by_units_in_price_in, ...rest } = obj;
              // Create a new object with the renamed key and the rest of the properties
              return { price_in: avg_weight_by_units_in_price_in, ...rest };
            }),
            metadataSlice["custom-projection"],
            "sum_"
          ),
          "price"
        ),
        crossAdjustments: currentTupleData.crossAdjustments,
      });
    }

    setSelectValue(value);
    setAnchorEl(null);
  };

  const handleOnClose = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <div
        role="presentation"
        onClick={handleSelectClick}
        className={classes.selectValueContainer}
      >
        <StyledBox>
          <Typography variant="body1">
            {selectValue.labelWithIcon
              ? selectValue.labelWithIcon
              : selectValue.label}
          </Typography>
        </StyledBox>

        <Box className={classes.selectValueIcon}>
          <BiChevronDown size={20} />
        </Box>
      </div>
      <Popover
        className={classes.popoverRoot}
        open={Boolean(anchorEl)}
        onClose={handleOnClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {selectTitle === "" ? null : (
          <Box
            component="span"
            className={`${classes.popoverTitle} ${classes.filterTitleAndIcon}`}
          >
            <Typography variant="body1" className={classes.alignCenterFlex}>
              <b>{selectTitle}</b>
            </Typography>
          </Box>
        )}

        <List className={classes.listRoot}>
          {_.map(selectOptions, (option) => {
            return (
              <ListItem
                onClick={() =>
                  handleListItemClick({
                    key: option.key,
                    label: option.label,
                    labelWithIcon: option.labelWithIcon,
                  })
                }
                className={classes.listItem}
                key={option.key}
              >
                {option.icon}
                <Typography variant="body1">
                  {option.labelWithIcon ? option.labelWithIcon : option.label}
                </Typography>
              </ListItem>
            );
          })}
        </List>
      </Popover>
    </>
  );
};

export default SelectOptionsComponent;
